/**
 * LeadDetailPage — page shell with tabbed layout.
 * Implementation lives here; LeadDetailPage.tsx is a one-line barrel re-export.
 *
 * New features (iteration 4):
 * - URL-synced active tab with stage-aware defaults (US-71, US-73)
 * - Stage-gated tab visibility (US-71)
 * - Count badges on Messages, Applicants, Verifications tabs (US-73)
 * - Stage stepper in header (US-71)
 * - Lazy per-tab data fetching (screening criteria, proposals, showings only load when tab active)
 *
 * Note: useSearchParams replaces all search params on tab click.
 * This route currently has no other query params, so it is safe.
 * If other params are ever added, this will need to merge rather than replace.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { ScrollFadeTabs } from '../../../components/ui/ScrollFadeTabs';
import { fetchLead, fetchApplicants, advanceLeadStage, markLeadRead, fetchListing, closeListing, markApplicantReviewed } from '../../../lib/leasing.api';
import { useDomainEvents } from '../../../hooks/useDomainEvents';
import { useMarkSeen } from '../../../hooks/useAwareness';
import { PropertyTimezoneProvider } from '../../../contexts/PropertyTimezoneContext';
import { deriveTimezone } from '../../../utils/date';
import { useUnitTimezone } from '../../../hooks/useUnitTimezone';
import { useListingExternalPosts } from '../../../hooks/useListingExternalPosts';
import { TakeDownNotice } from '../../../components/leasing/TakeDownNotice';
import type { LeadStage, Applicant } from './shared';
import { LeadDetailHeader } from './LeadDetailHeader';
import { OverviewTab } from './OverviewTab';
import { ApplicantsTab } from './ApplicantsTab';
import { ShowingsTab } from './ShowingsTab';
import { VerificationsTab } from './VerificationsTab';
import { MessagesTab } from './MessagesTab';
import { ActivityTab } from './ActivityTab';

// ---------------------------------------------------------------------------
// Stage order map (used for tab visibility — US-71)
// ---------------------------------------------------------------------------

const STAGE_ORDER: Record<LeadStage, number> = {
  inquiring: 0,
  showing: 1,
  applying: 2,
  verifying: 3,
  deciding: 4,
  closed: 5,
};

// ---------------------------------------------------------------------------
// defaultTabForStage — URL-synced tab default per stage
// ---------------------------------------------------------------------------

function defaultTabForStage(stage: LeadStage | undefined): string {
  if (!stage) return 'messages';
  switch (stage) {
    case 'inquiring': return 'messages';
    case 'showing': return 'showings';
    case 'applying': return 'applicants';
    case 'verifying': return 'verifications';
    case 'deciding':
    case 'closed':
      return 'overview';
    default:
      return 'messages';
  }
}

// ---------------------------------------------------------------------------
// LeadDetailPage
// ---------------------------------------------------------------------------

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // URL-synced tab state
  // Note: setSearchParams({ tab: v }) replaces all search params.
  // This is safe because this route has no other query params currently.
  const [searchParams, setSearchParams] = useSearchParams();

  const [isAdvancing, setIsAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [isClosingListing, setIsClosingListing] = useState(false);
  const [closeListingError, setCloseListingError] = useState<string | null>(null);

  // Detect platform_admin role from a data attribute injected by the server into the root HTML.
  // Falls back to false so non-admin users never see the purge button.
  const isPlatformAdmin = (() => {
    try {
      const meta = document.querySelector<HTMLMetaElement>('meta[name="x-user-roles"]');
      return meta?.content?.split(',').includes('platform_admin') ?? false;
    } catch {
      return false;
    }
  })();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => fetchLead(id!),
    enabled: !!id,
  });

  const lead = data?.lead;

  // The lead detail response does not carry the unit id, so it comes from the lead's listing
  // (also needed for the "Close listing" prompt).
  const { data: listingData } = useQuery({
    queryKey: ['listing', lead?.listingId],
    queryFn: () => fetchListing(lead!.listingId),
    enabled: !!lead?.listingId,
    staleTime: 60_000,
  });

  // Sourced by unit ID via the batched cache (issue #910) — not from lead.unitState,
  // which requires whoever built the payload to have remembered to select it.
  const timezoneQuery = useUnitTimezone(listingData?.listing?.unitId);
  const listingId = listingData?.listing?.id ?? null;
  // listing-marketing-hub BR-15c: the listing's posts still live on other sites once it is not
  // active — including when approving this lead's applicant closed it.
  const { data: listingPosts } = useListingExternalPosts(listingId);
  const listingIsLive =
    listingData?.listing?.status === 'active' || listingData?.listing?.status === 'paused';

  const handleCloseListing = async () => {
    if (!listingId) return;
    setIsClosingListing(true);
    setCloseListingError(null);
    try {
      // This button only retries closing the listing after decide()'s own best-effort
      // auto-close failed (see decide()'s try/catch in lead.service.ts) — it is not a fresh
      // manual-close decision, so it must NOT re-run the lead cascade: that would silently
      // override whatever closeSiblings choice ('all'/'select'/'none') the landlord already
      // made moments earlier as part of the approval (Finding 4).
      await closeListing(listingId, { skipLeadCascade: true });
      await queryClient.invalidateQueries({ queryKey: ['listing', lead?.listingId] });
    } catch (err) {
      setCloseListingError(err instanceof Error ? err.message : 'Failed to close listing');
    } finally {
      setIsClosingListing(false);
    }
  };

  // URL-synced active tab with stage-aware default
  const activeTab = searchParams.get('tab') ?? defaultTabForStage(lead?.stage);

  // Sync URL: when no ?tab= is present and the lead has loaded, write the default tab
  // into the URL so that the URL always reflects the active tab. Without this, clicking
  // the already-active default tab does nothing (Radix won't fire onValueChange for the
  // same value), so the URL would never update.
  useEffect(() => {
    if (lead && !searchParams.get('tab')) {
      const defaultTab = defaultTabForStage(lead.stage);
      setSearchParams({ tab: defaultTab }, { replace: true });
    }
  }, [lead?.id, lead?.stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch applicants for stage-advance gating (US-44) and badge counts.
  // Constraint R-IT4-3: enabled condition must be exactly this — do NOT gate on activeTab.
  // ApplicantsTab has a separate query with the same key; TanStack deduplicates network calls.
  const { data: headerApplicantsData } = useQuery({
    queryKey: ['applicants', id],
    queryFn: () => fetchApplicants(id!),
    enabled: !!id && !!lead && !['inquiring', 'showing', 'closed'].includes(lead.stage),
    staleTime: 30_000,
  });
  const headerApplicants: Applicant[] = headerApplicantsData ?? [];

  // markLeadRead fire-and-forget effect.
  // Constraint R-IT4-8: This must remain in index.tsx, NOT in any tab component.
  // Fires on page load regardless of active tab.
  const hasUnread = lead?.hasUnread ?? false;
  useEffect(() => {
    if (id && hasUnread) {
      void markLeadRead(id).then(() => {
        void queryClient.invalidateQueries({ queryKey: ['inbox-unresponded-count'] });
        void queryClient.invalidateQueries({ queryKey: ['inbox-unresponded'] });
        void queryClient.invalidateQueries({ queryKey: ['leads'] });
      });
    }
  }, [id, lead?.id, hasUnread, queryClient]);

  const invalidateLead = () => {
    void queryClient.invalidateQueries({ queryKey: ['lead', id] });
  };

  // leasing-support/BR-38, BR-AT-18 — mark an individual applicant's application
  // reviewed the moment its detail is opened. ApplicantsTab.tsx renders the per-applicant
  // "View application data" toggle (ApplicantDetailPanel) with
  // aria-controls="applicant-detail-<applicantId>" / aria-expanded; we hook it here via
  // event delegation on the Applicants tab content rather than editing ApplicantsTab.tsx
  // (out of this layer's scope). Uses the capture phase so aria-expanded still reflects
  // the PRE-click state (the child's own onClick, which flips it, runs during bubble,
  // after this handler) — so we only fire on the click that OPENS the panel, not the one
  // that closes it. Fire-and-forget and idempotent server-side; never blocks the view.
  const handleApplicantsTabClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!id) return;
      const toggle = (e.target as HTMLElement).closest<HTMLElement>(
        '[aria-controls^="applicant-detail-"]'
      );
      if (!toggle) return;
      if (toggle.getAttribute('aria-expanded') === 'true') return; // this click closes it
      const applicantId = toggle.getAttribute('aria-controls')!.replace('applicant-detail-', '');
      markApplicantReviewed(id, applicantId).catch((err) => {
        console.error('Failed to mark applicant reviewed', err);
      });
    },
    [id]
  );

  // BR-16 — opening this lead's detail page marks ITS awareness items seen (list pages, like
  // LeadInboxPage, do not). referenceType 'lead' matches the leasing_pipeline registry
  // classes' declared referenceType (showing_requested, applicant_submitted, etc — MF-BR-77).
  const markSeen = useMarkSeen();
  useEffect(() => {
    if (id) {
      markSeen.mutate({ referenceType: 'lead', referenceId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // DC-2 — re-keyed from the old onNewLeadMessage's `data.leadId` guard to `event.caseId`:
  // case.message_received carries `caseId`, not `leadId`. Lead.id IS Case.id (same value), so
  // the guard is equivalent — but the field renamed, and a missed rename here would silently
  // stop this tab updating (BR-16 test-plan note).
  const invalidateLeadOnCaseMessage = useCallback(
    (event: { caseId: string }) => {
      if (event.caseId === id) {
        void queryClient.invalidateQueries({ queryKey: ['lead', id] });
      }
    },
    [id, queryClient]
  );
  const invalidateApplicantsOnSubmit = useCallback(
    (event: { leadId: string }) => {
      if (event.leadId === id) {
        void queryClient.invalidateQueries({ queryKey: ['applicants', id] });
      }
    },
    [id, queryClient]
  );
  // BR-16/LS-BR-NOTIF-14 (SAGA-8) — OverviewTab's "Pending proposals" panel (['proposals', id])
  // is a SEPARATE query from the Showings tab's (['listing', ..., 'showings', 'for-lead', id]).
  // A proposal lifecycle event (prospect-initiated create/reschedule/cancel, or landlord-
  // initiated confirm/reject) can change BOTH — e.g. a prospect cancelling their own pending
  // proposal must disappear from the Overview panel live, not just leave the Showings tab
  // alone. Invalidating only the showings key here previously left proposals-panel content
  // stale until its own 30s staleTime elapsed.
  const invalidateProposalsAndShowingsOnLeadEvent = useCallback(
    (event: { leadId: string }) => {
      if (event.leadId === id) {
        void queryClient.invalidateQueries({ queryKey: ['proposals', id] });
        if (lead?.listingId) {
          void queryClient.invalidateQueries({
            queryKey: ['listing', lead.listingId, 'showings', 'for-lead', id],
          });
        }
      }
    },
    [id, lead?.listingId, queryClient]
  );
  const invalidateShowingsOnLeadEvent = useCallback(
    (event: { leadId: string }) => {
      if (event.leadId === id && lead?.listingId) {
        void queryClient.invalidateQueries({
          queryKey: ['listing', lead.listingId, 'showings', 'for-lead', id],
        });
      }
    },
    [id, lead?.listingId, queryClient]
  );

  useDomainEvents({
    'case.message_received': invalidateLeadOnCaseMessage,
    'applicant.submitted': invalidateApplicantsOnSubmit,
    'appointment_proposal.created': invalidateProposalsAndShowingsOnLeadEvent,
    'appointment_proposal.confirmed': invalidateProposalsAndShowingsOnLeadEvent,
    'appointment_proposal.rejected': invalidateProposalsAndShowingsOnLeadEvent,
    'appointment_proposal.cancelled': invalidateProposalsAndShowingsOnLeadEvent,
    'appointment_proposal.rescheduled': invalidateProposalsAndShowingsOnLeadEvent,
    'showing.scheduled': invalidateShowingsOnLeadEvent,
  });

  const handleAdvance = async (to: LeadStage) => {
    if (!id) return;
    setAdvanceError(null);
    setIsAdvancing(true);
    try {
      await advanceLeadStage(id, to);
      invalidateLead();
    } catch (err) {
      setAdvanceError(err instanceof Error ? err.message : 'Failed to advance stage');
    } finally {
      setIsAdvancing(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Tab visibility logic (US-71)
  // ---------------------------------------------------------------------------

  const currentStageOrder = lead ? STAGE_ORDER[lead.stage] : -1;

  // Showings: visible from the inquiring stage onward. Scheduling a showing is
  // a primary action for a brand-new lead — and it's the very action that
  // advances inquiring -> showing. Gating the tab behind the showing stage
  // created a chicken-and-egg gap: an inquiring lead could never schedule its
  // first showing from the lead page. Always reachable (inquiring is the
  // lowest stage, so this is effectively always visible).
  const showShowingsTab = currentStageOrder >= STAGE_ORDER['inquiring'];

  // Applicants: visible from the inquiring stage onward. Adding the first applicant is a
  // primary action for a brand-new lead — and it's the action that advances the lead
  // inquiring/showing -> applying (US-67). The add-applicants form lives only on this tab,
  // so gating the tab behind the applying stage created a chicken-and-egg gap: an inquiring
  // lead could never get its first applicant from the lead page. Mirrors the Showings tab;
  // always reachable since inquiring is the lowest stage.
  const showApplicantsTab = currentStageOrder >= STAGE_ORDER['inquiring'];

  // Verifications: visible if stage >= verifying OR any applicant has started/submitted
  const showVerificationsTab =
    currentStageOrder >= STAGE_ORDER['verifying'] ||
    headerApplicants.some((a) => a.status === 'started' || a.status === 'submitted');

  // ---------------------------------------------------------------------------
  // Tab count badges (US-73)
  // ---------------------------------------------------------------------------

  // Messages badge: inbound messages that need a reply
  const unreadCount = (lead?.messages ?? []).filter(
    (m) => m.direction === 'inbound' && m.routingDecision === 'created' && m.status === 'pending'
  ).length;

  // Applicants badge: count of applicants (shown if > 0)
  const applicantBadge = headerApplicants.length;

  // Verifications badge: count non-waived tasks not yet resolved (pending/in-progress).
  // AC-L7: also exclude 'invited' applicants — a task for someone who hasn't started/
  // submitted yet isn't landlord-actionable "pending" in the same sense.
  const verificationsPendingCount = headerApplicants
    .filter((a) => a.status !== 'withdrawn')
    .filter((a) => a.status !== 'invited')
    .flatMap((a) => a.verificationTasks)
    .filter((t) => t.status !== 'waived' && t.status !== 'passed' && t.status !== 'failed')
    .length;

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div
        data-testid="lead-detail-page"
        className="max-w-3xl mx-auto pb-6 px-[var(--page-padding-x)]"
      >
        <Link
          to="/leasing/leads"
          data-testid="lead-detail-back"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          All Leads
        </Link>
        <div className="h-8 mb-4">
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-40 w-full mb-4" />
        <Skeleton className="h-64 w-full mb-4" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error || !lead) {
    const errorMessage =
      error != null && 'status' in error && (error as { status?: number }).status === 404
        ? 'Lead not found.'
        : 'Something went wrong loading this lead. Please try again.';
    return (
      <div
        data-testid="lead-detail-page"
        className="max-w-3xl mx-auto pb-6 px-[var(--page-padding-x)]"
      >
        <Link
          to="/leasing/leads"
          data-testid="lead-detail-back"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          All Leads
        </Link>
        <div className="py-12 text-center">
          <p
            data-testid="lead-detail-error"
            className="text-destructive mb-4"
          >
            {errorMessage}
          </p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const propertyTimezone = timezoneQuery.data ?? deriveTimezone('');

  return (
    <PropertyTimezoneProvider timezone={propertyTimezone}>
    <div
      data-testid="lead-detail-page"
      className="max-w-3xl mx-auto pb-6 px-[var(--page-padding-x)]"
    >
      {/* Back link */}
      <Link
        to="/leasing/leads"
        data-testid="lead-detail-back"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        All Leads
      </Link>

      {/* Advance error */}
      {advanceError && (
        <div
          data-testid="lead-advance-error"
          className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          {advanceError}
        </div>
      )}

      {/* Sticky header with stage stepper */}
      <LeadDetailHeader
        lead={lead}
        applicants={headerApplicants}
        onAdvance={(to) => void handleAdvance(to)}
        isAdvancing={isAdvancing}
        isPlatformAdmin={isPlatformAdmin}
        onLeadUpdated={invalidateLead}
      />

      {/* Close-listing prompt — approved, but the listing is still live */}
      {lead.decision === 'approved_lease_started' && listingIsLive && (
        <div
          className="mb-4 flex items-center gap-3 px-1 py-3 border border-warning/40 bg-warning/5 rounded-lg"
          data-testid="lead-close-listing-prompt"
        >
          <div className="flex-1 min-w-0 px-3">
            <p className="text-sm font-medium text-foreground">Listing is still live</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              This application is approved. Close the listing so it stops taking new applications
              and showings.
            </p>
            {closeListingError && (
              <p className="text-sm text-destructive mt-1" role="alert">
                {closeListingError}
              </p>
            )}
          </div>
          <Button
            variant="default"
            size="sm"
            data-testid="lead-close-listing-btn"
            disabled={isClosingListing}
            onClick={() => void handleCloseListing()}
            className="shrink-0 mr-3"
            aria-label="Close the listing for this unit"
          >
            {isClosingListing ? 'Closing…' : 'Close listing'}
          </Button>
        </div>
      )}

      {listingData?.listing && (
        <div className="mb-4 empty:hidden">
          <TakeDownNotice
            listingId={listingData.listing.id}
            status={listingData.listing.status}
            posts={listingPosts}
            onListingPage={false}
          />
        </div>
      )}

      {/* Tabbed body */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setSearchParams({ tab: v })}
      >
        <ScrollFadeTabs activeValue={activeTab}>
          <TabsList data-testid="lead-tab-bar">
            {/* Overview — always visible */}
            <TabsTrigger value="overview" data-testid="tab-trigger-overview">
              Overview
            </TabsTrigger>

            {/* Applicants — visible if stage >= applying or has applicants */}
            {showApplicantsTab && (
              <TabsTrigger value="applicants" data-testid="tab-trigger-applicants">
                Applicants
                {applicantBadge > 0 && (
                  <span
                    data-testid="applicants-badge"
                    className="ml-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-medium bg-muted text-muted-foreground"
                    aria-label={`${applicantBadge} applicants`}
                  >
                    {applicantBadge}
                  </span>
                )}
              </TabsTrigger>
            )}

            {/* Showings — visible if stage >= showing */}
            {showShowingsTab && (
              <TabsTrigger value="showings" data-testid="tab-trigger-showings">
                Showings
              </TabsTrigger>
            )}

            {/* Verifications — visible if stage >= verifying or any applicant started/submitted */}
            {showVerificationsTab && (
              <TabsTrigger value="verifications" data-testid="tab-trigger-verifications">
                Verifications
                {verificationsPendingCount > 0 && (
                  <span
                    data-testid="verifications-badge"
                    className="ml-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-medium bg-warning/20 text-warning-foreground"
                    aria-label={`${verificationsPendingCount} pending verifications`}
                  >
                    {verificationsPendingCount}
                  </span>
                )}
              </TabsTrigger>
            )}

            {/* Messages — always visible */}
            <TabsTrigger value="messages" data-testid="tab-trigger-messages">
              Messages
              {unreadCount > 0 && (
                <span
                  data-testid="messages-badge"
                  className="ml-1.5 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-medium bg-destructive text-destructive-foreground"
                  aria-label={`${unreadCount} unread`}
                >
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>

            {/* Activity — always visible */}
            <TabsTrigger value="activity" data-testid="tab-trigger-activity">
              Activity
            </TabsTrigger>
          </TabsList>
        </ScrollFadeTabs>

        <TabsContent value="overview">
          <OverviewTab
            lead={lead}
            onLeadUpdated={invalidateLead}
            isActive={activeTab === 'overview'}
          />
        </TabsContent>

        {showApplicantsTab && (
          <TabsContent value="applicants" onClickCapture={handleApplicantsTabClickCapture}>
            <ApplicantsTab
              lead={lead}
              onLeadUpdated={invalidateLead}
              isActive={activeTab === 'applicants'}
            />
          </TabsContent>
        )}

        {showShowingsTab && (
          <TabsContent value="showings">
            <ShowingsTab
              lead={lead}
              onRefresh={invalidateLead}
              isActive={activeTab === 'showings'}
            />
          </TabsContent>
        )}

        {showVerificationsTab && (
          <TabsContent value="verifications">
            <VerificationsTab
              lead={lead}
              isActive={activeTab === 'verifications'}
            />
          </TabsContent>
        )}

        <TabsContent value="messages">
          <MessagesTab
            leadId={lead.id}
            messages={lead.messages}
            leadName={lead.prospectName ?? lead.prospectEmail}
            propertyAddress={lead.propertyAddress}
            onMessageSent={invalidateLead}
          />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab events={lead.events} />
        </TabsContent>
      </Tabs>
    </div>
    </PropertyTimezoneProvider>
  );
}
